import * as plugins from './plugins.js';
import {
  VirtualStream,
  VirtualStreamActivationScope,
  VirtualStreamOpenTimeoutError,
} from './classes.virtualstream.js';
import { TypedResponseError } from './classes.typedresponseerror.js';
import { dispatchTypedRequestHook } from './internal.hooks.js';
import { TypedTarget } from './classes.typedtarget.js';

const webrequestInstance = new plugins.webrequest.WebrequestClient();
const maxTimeoutMs = 2_147_483_647;

export interface ITypedRequestFireOptions {
  useCache?: boolean;
  timeoutMs?: number;
  maxRetries?: number;
  abortSignal?: AbortSignal;
}

interface IResolvedTypedRequestFireOptions {
  useCache: boolean;
  timeoutMs?: number;
  maxRetries: number;
  deadlineAt?: number;
  cacheKey?: string;
  abortSignal?: AbortSignal;
}

interface IRequestIdentitySnapshot {
  readonly method: string;
  readonly correlationId?: string;
  readonly requestInstanceId?: string;
}

export class TypedRequestTimeoutError extends Error {
  public readonly method: string;
  public readonly timeoutMs: number;

  constructor(methodArg: string, timeoutMsArg: number) {
    super(`Typed request "${methodArg}" timed out after ${timeoutMsArg}ms`);
    this.name = 'TypedRequestTimeoutError';
    this.method = methodArg;
    this.timeoutMs = timeoutMsArg;
  }
}

export class TypedRequestRetryLimitError extends Error {
  public readonly method: string;
  public readonly maxRetries: number;

  constructor(methodArg: string, maxRetriesArg: number) {
    super(
      `Typed request "${methodArg}" exceeded its ${maxRetriesArg} server-directed retries`
    );
    this.name = 'TypedRequestRetryLimitError';
    this.method = methodArg;
    this.maxRetries = maxRetriesArg;
  }
}

export class TypedRequest<T extends plugins.typedRequestInterfaces.ITypedRequest> {
  /**
   * in case we post against a url endpoint
   */
  public urlEndPoint?: string;

  /**
   * in case we post against a TypedTarget
   */
  typedTarget!: TypedTarget;

  public method: string;

  /**
   * When true, hooks will not be called for this request.
   * Use this for internal/logging requests to prevent infinite loops.
   */
  public skipHooks: boolean = false;

  /**
   * @param postEndPointArg
   * @param methodArg
   */
  constructor(postTarget: string | TypedTarget, methodArg: T['method']) {
    if (typeof postTarget === 'string') {
      this.urlEndPoint = postTarget;
    } else {
      this.typedTarget = postTarget;
    }
    this.method = methodArg;
  }

  /**
   * fires the request
   */
  public async fire(
    fireArg: T['request'],
    fireOptionsArg: boolean | ITypedRequestFireOptions = false
  ): Promise<T['response']> {
    const requestStartTime = Date.now();
    const fireOptions = this.resolveFireOptions(fireOptionsArg);
    const abortContext = this.createAbortContext(fireOptions);
    fireOptions.abortSignal = abortContext.signal;
    const activationScope = new VirtualStreamActivationScope();

    let payloadSending: plugins.typedRequestInterfaces.ITypedRequest = {
      method: this.method,
      request: fireArg,
      response: null as unknown as T['response'],
      requestInstanceId: plugins.isounique.uni(),
      correlation: {
        id: plugins.isounique.uni(),
        phase: 'request',
      },
    };

    try {
      this.assertActive(fireOptions);
      const encodedPayload =
        VirtualStream.encodePayloadForNetworkWithMetadata(payloadSending, {
          side: 'requesting',
          activationScope,
          virtualStreamTransport:
            this.typedTarget?.options.virtualStreamTransport,
        });
      payloadSending = encodedPayload.payload;

      const payloadContainsVirtualStream =
        encodedPayload.containsNetworkDescriptor;
      if (
        payloadContainsVirtualStream &&
        !this.urlEndPoint &&
        this.typedTarget.options.supportsAbortSignal !== true
      ) {
        throw new Error(
          'VirtualStreams require TypedTarget AbortSignal transport support'
        );
      }
      if (
        fireOptions.useCache &&
        payloadContainsVirtualStream
      ) {
        throw new Error(
          'TypedRequest response caching is not supported for requests containing VirtualStreams'
        );
      }
      this.assertActive(fireOptions);
      if (fireOptions.useCache) {
        fireOptions.cacheKey = await this.createCacheKey(payloadSending);
        this.assertActive(fireOptions);
      }

      if (!this.skipHooks) {
        dispatchTypedRequestHook('onOutgoingRequest', {
          correlationId: payloadSending.correlation!.id,
          method: this.method,
          direction: 'outgoing',
          phase: 'request',
          timestamp: requestStartTime,
          payload: fireArg,
        });
      }

      activationScope.publish();
      let payloadReceiving = await this.postTrObject(
        payloadSending,
        fireOptions
      );
      delete payloadReceiving.localData;
      const finalResponseError = payloadReceiving.error;
      this.assertActive(fireOptions);

      if (finalResponseError) {
        if (!this.skipHooks) {
          dispatchTypedRequestHook('onIncomingResponse', {
            correlationId: payloadSending.correlation!.id,
            method: this.method,
            direction: 'incoming',
            phase: 'response',
            timestamp: Date.now(),
            durationMs: Date.now() - requestStartTime,
            payload: payloadReceiving.response,
            errorText: finalResponseError.text,
          });
        }
        this.assertActive(fireOptions);
        throw new TypedResponseError(
          finalResponseError.text,
          finalResponseError.data
        );
      }

      let response = payloadReceiving.response as T['response'];
      if (
        !this.urlEndPoint
        && this.typedTarget.options.supportsAbortSignal !== true
        && VirtualStream.containsNetworkDescriptor(response)
      ) {
        throw new Error(
          'VirtualStreams require TypedTarget AbortSignal transport support'
        );
      }
      try {
        response = await VirtualStream.decodePayloadFromNetworkAsync(
          response,
          {
            side: 'requesting',
            activationScope,
            virtualStreamTransport:
              this.typedTarget?.options.virtualStreamTransport,
            openDeadlineAt: fireOptions.deadlineAt,
            abortSignal: fireOptions.abortSignal,
          }
        );
      } catch (error) {
        if (
          error instanceof VirtualStreamOpenTimeoutError
          && fireOptions.deadlineAt !== undefined
          && error.deadlineAt === fireOptions.deadlineAt
        ) {
          throw this.createTimeoutError(fireOptions);
        }
        throw error;
      }
      this.assertActive(fireOptions);

      activationScope.authorizeApplicationUse();
      if (!this.skipHooks) {
        dispatchTypedRequestHook('onIncomingResponse', {
          correlationId: payloadSending.correlation!.id,
          method: this.method,
          direction: 'incoming',
          phase: 'response',
          timestamp: Date.now(),
          durationMs: Date.now() - requestStartTime,
          payload: response,
        });
      }
      this.assertActive(fireOptions);

      activationScope.commit();
      return response;
    } catch (error) {
      await activationScope.rollback(error);
      throw error;
    } finally {
      abortContext.cleanup();
    }
  }

  private async postTrObject(
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest,
    fireOptionsArg: IResolvedTypedRequestFireOptions = {
      useCache: false,
      maxRetries: 3,
    },
  ): Promise<plugins.typedRequestInterfaces.ITypedRequest> {
    let retryCount = 0;
    while (true) {
      this.assertActive(fireOptionsArg);
      const requestIdentity = this.createRequestIdentitySnapshot(payloadSendingArg);
      let payloadReceiving: plugins.typedRequestInterfaces.ITypedRequest;
      if (this.urlEndPoint) {
        let responseResult: plugins.webrequest.IWebrequestJsonResult<
          plugins.typedRequestInterfaces.ITypedRequest
        >;
        try {
          responseResult = await webrequestInstance.postJsonWithMetadata(
            this.urlEndPoint,
            payloadSendingArg,
            {
              cacheStrategy: fireOptionsArg.useCache
                ? 'cache-first'
                : 'network-only',
              cacheKey: fireOptionsArg.cacheKey,
              timeout: this.getRemainingTimeout(fireOptionsArg),
              signal: fireOptionsArg.abortSignal,
            }
          );
        } catch (error) {
          if (fireOptionsArg.cacheKey && error instanceof SyntaxError) {
            await webrequestInstance.deleteCache(fireOptionsArg.cacheKey);
          }
          if (fireOptionsArg.abortSignal?.aborted) {
            throw this.getAbortReason(fireOptionsArg);
          }
          if (
            fireOptionsArg.timeoutMs !== undefined &&
            (this.hasDeadlineExpired(fireOptionsArg) ||
              plugins.webrequest.isWebrequestTimeoutError(error))
          ) {
            throw this.createTimeoutError(fireOptionsArg);
          }
          throw error;
        }
        payloadReceiving = responseResult.data;
        try {
          if (responseResult.metadata.fromCache) {
            payloadReceiving = this.reconstructCachedHttpResponse(
              payloadReceiving,
              payloadSendingArg,
              requestIdentity,
            );
          } else {
            this.assertResponseEnvelope(payloadReceiving, requestIdentity);
          }
        } catch (error) {
          if (fireOptionsArg.cacheKey) {
            await webrequestInstance.deleteCache(fireOptionsArg.cacheKey);
          }
          throw error;
        }
        if (
          fireOptionsArg.cacheKey
          && !this.isReusableCachedHttpEnvelope(payloadReceiving)
        ) {
          await webrequestInstance.deleteCache(fireOptionsArg.cacheKey);
        }
      } else {
        payloadReceiving = await this.postTypedTarget(
          payloadSendingArg,
          fireOptionsArg
        );
      }
      this.assertResponseEnvelope(payloadReceiving, requestIdentity);
      this.assertActive(fireOptionsArg);
      if (payloadReceiving.error) {
        if (!payloadReceiving.retry) {
          return payloadReceiving;
        }
      }
      if (!payloadReceiving.retry) {
        return payloadReceiving;
      }
      console.log(`Server requested a retry for TypedRequest method '${this.method}'`);
      if (retryCount >= fireOptionsArg.maxRetries) {
        throw new TypedRequestRetryLimitError(
          this.method,
          fireOptionsArg.maxRetries
        );
      }
      retryCount += 1;
      await this.waitForRetry(payloadReceiving.retry.waitForMs, fireOptionsArg);
      payloadSendingArg.requestInstanceId = plugins.isounique.uni();
      payloadSendingArg.correlation!.phase = 'request';
      payloadSendingArg.response = null as unknown as T['response'];
      delete payloadSendingArg.error;
      delete payloadSendingArg.retry;
    }
  }

  private assertResponseEnvelope(
    payloadReceivingArg: plugins.typedRequestInterfaces.ITypedRequest,
    requestIdentityArg: IRequestIdentitySnapshot,
  ): void {
    const responseError = payloadReceivingArg?.error;
    const requestInstanceId = payloadReceivingArg?.requestInstanceId;
    const expectedRequestInstanceId = requestIdentityArg.requestInstanceId;
    if (
      !payloadReceivingArg
      || typeof payloadReceivingArg !== 'object'
      || payloadReceivingArg.method !== requestIdentityArg.method
      || payloadReceivingArg.correlation?.phase !== 'response'
      || typeof payloadReceivingArg.correlation.id !== 'string'
      || payloadReceivingArg.correlation.id.length === 0
      || !Object.hasOwn(payloadReceivingArg, 'response')
      || typeof requestInstanceId !== 'string'
      || requestInstanceId.length === 0
      || typeof expectedRequestInstanceId !== 'string'
      || expectedRequestInstanceId.length === 0
      || requestInstanceId !== expectedRequestInstanceId
      || (
        responseError !== undefined
        && (
          !responseError
          || typeof responseError !== 'object'
          || typeof responseError.text !== 'string'
        )
      )
      || payloadReceivingArg.correlation.id !== requestIdentityArg.correlationId
    ) {
      throw new TypeError('TypedRequest received an invalid response envelope');
    }
  }

  private async postTypedTarget(
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest,
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): Promise<plugins.typedRequestInterfaces.ITypedRequest> {
    this.assertActive(fireOptionsArg);
    try {
      return await this.typedTarget.post(payloadSendingArg, {
        signal: fireOptionsArg.abortSignal,
      });
    } catch (error) {
      if (fireOptionsArg.abortSignal?.aborted) {
        throw this.getAbortReason(fireOptionsArg);
      }
      throw error;
    }
  }

  private resolveFireOptions(
    fireOptionsArg: boolean | ITypedRequestFireOptions
  ): IResolvedTypedRequestFireOptions {
    const fireOptions =
      typeof fireOptionsArg === 'boolean'
        ? { useCache: fireOptionsArg }
        : fireOptionsArg;

    if (!fireOptions || typeof fireOptions !== 'object') {
      throw new TypeError('TypedRequest fire options must be a boolean or an options object');
    }
    if (
      fireOptions.useCache !== undefined &&
      typeof fireOptions.useCache !== 'boolean'
    ) {
      throw new TypeError('TypedRequest useCache must be a boolean');
    }
    if (
      fireOptions.timeoutMs !== undefined &&
      (!Number.isSafeInteger(fireOptions.timeoutMs) ||
        fireOptions.timeoutMs <= 0 ||
        fireOptions.timeoutMs > maxTimeoutMs)
    ) {
      throw new RangeError(
        `TypedRequest timeoutMs must be a positive safe integer no greater than ${maxTimeoutMs}`
      );
    }
    if (
      fireOptions.abortSignal !== undefined
      && !this.isAbortSignal(fireOptions.abortSignal)
    ) {
      throw new TypeError('TypedRequest abortSignal must be an AbortSignal');
    }
    if (
      fireOptions.maxRetries !== undefined &&
      (!Number.isSafeInteger(fireOptions.maxRetries) ||
        fireOptions.maxRetries < 0 ||
        fireOptions.maxRetries > 1_000)
    ) {
      throw new RangeError(
        'TypedRequest maxRetries must be a safe integer between 0 and 1000'
      );
    }
    if (
      (fireOptions.timeoutMs !== undefined || fireOptions.abortSignal !== undefined) &&
      !this.urlEndPoint &&
      this.typedTarget.options.supportsAbortSignal !== true
    ) {
      throw new Error(
        fireOptions.abortSignal
          ? 'TypedRequest cancellation requires an HTTP URL or a TypedTarget with AbortSignal support'
          : 'TypedRequest timeoutMs requires an HTTP URL or a TypedTarget with AbortSignal support'
      );
    }

    return {
      useCache: fireOptions.useCache ?? false,
      timeoutMs: fireOptions.timeoutMs,
      maxRetries: fireOptions.maxRetries ?? 3,
      abortSignal: fireOptions.abortSignal,
      deadlineAt:
        fireOptions.timeoutMs === undefined
          ? undefined
          : performance.now() + fireOptions.timeoutMs,
    };
  }

  private getRemainingTimeout(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): number | undefined {
    if (
      fireOptionsArg.deadlineAt === undefined ||
      fireOptionsArg.timeoutMs === undefined
    ) {
      return undefined;
    }
    const remainingMs = Math.ceil(
      fireOptionsArg.deadlineAt - performance.now()
    );
    if (remainingMs <= 0) {
      throw this.createTimeoutError(fireOptionsArg);
    }
    return remainingMs;
  }

  private assertDeadline(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): void {
    this.getRemainingTimeout(fireOptionsArg);
  }

  private assertActive(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): void {
    if (fireOptionsArg.abortSignal?.aborted) {
      throw this.getAbortReason(fireOptionsArg);
    }
    this.assertDeadline(fireOptionsArg);
  }

  private hasDeadlineExpired(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): boolean {
    return (
      fireOptionsArg.deadlineAt !== undefined &&
      performance.now() >= fireOptionsArg.deadlineAt
    );
  }

  private createTimeoutError(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): TypedRequestTimeoutError {
    return new TypedRequestTimeoutError(
      this.method,
      fireOptionsArg.timeoutMs!
    );
  }

  private async waitForRetry(
    waitForMsArg: number,
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): Promise<void> {
    if (
      !Number.isSafeInteger(waitForMsArg) ||
      waitForMsArg <= 0 ||
      waitForMsArg > maxTimeoutMs
    ) {
      throw new RangeError(
        `TypedRequest retry waitForMs must be a positive safe integer no greater than ${maxTimeoutMs}`
      );
    }
    this.assertActive(fireOptionsArg);
    const remainingMs = this.getRemainingTimeout(fireOptionsArg);
    if (remainingMs === undefined) {
      await this.delayWithAbort(waitForMsArg, fireOptionsArg);
      return;
    }
    if (waitForMsArg >= remainingMs) {
      await this.delayWithAbort(remainingMs, fireOptionsArg);
      throw this.createTimeoutError(fireOptionsArg);
    }
    await this.delayWithAbort(waitForMsArg, fireOptionsArg);
    this.assertActive(fireOptionsArg);
  }

  private async delayWithAbort(
    delayMsArg: number,
    fireOptionsArg: IResolvedTypedRequestFireOptions,
  ): Promise<void> {
    const signal = fireOptionsArg.abortSignal;
    if (!signal) {
      await plugins.smartdelay.delayFor(delayMsArg);
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, delayMsArg);
      const onAbort = () => {
        clearTimeout(timeout);
        signal.removeEventListener('abort', onAbort);
        reject(this.getAbortReason(fireOptionsArg));
      };
      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) onAbort();
    });
  }

  private createAbortContext(
    fireOptionsArg: IResolvedTypedRequestFireOptions,
  ): { signal?: AbortSignal; cleanup: () => void } {
    const callerSignal = fireOptionsArg.abortSignal;
    if (fireOptionsArg.timeoutMs === undefined) {
      return { signal: callerSignal, cleanup: () => {} };
    }

    const abortController = new AbortController();
    const onCallerAbort = () => {
      abortController.abort(
        callerSignal?.reason
          ?? new DOMException('The operation was aborted', 'AbortError'),
      );
    };
    if (callerSignal?.aborted) {
      onCallerAbort();
    } else {
      callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
    }
    const remainingTimeoutMs = Math.max(
      0,
      Math.ceil(fireOptionsArg.deadlineAt! - performance.now()),
    );
    const timeout = setTimeout(() => {
      abortController.abort(this.createTimeoutError(fireOptionsArg));
    }, remainingTimeoutMs);
    timeout.unref?.();
    return {
      signal: abortController.signal,
      cleanup: () => {
        clearTimeout(timeout);
        callerSignal?.removeEventListener('abort', onCallerAbort);
      },
    };
  }

  private getAbortReason(
    fireOptionsArg: IResolvedTypedRequestFireOptions,
  ): unknown {
    return fireOptionsArg.abortSignal?.reason
      ?? new DOMException('The operation was aborted', 'AbortError');
  }

  private reconstructCachedHttpResponse(
    cachedEnvelopeArg: plugins.typedRequestInterfaces.ITypedRequest,
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest,
    requestIdentityArg: IRequestIdentitySnapshot,
  ): plugins.typedRequestInterfaces.ITypedRequest {
    if (
      !cachedEnvelopeArg
      || typeof cachedEnvelopeArg !== 'object'
      || Array.isArray(cachedEnvelopeArg)
      || cachedEnvelopeArg.method !== requestIdentityArg.method
      || !Object.hasOwn(cachedEnvelopeArg, 'response')
      || !cachedEnvelopeArg.response
      || typeof cachedEnvelopeArg.response !== 'object'
      || !this.isReusableCachedHttpEnvelope(cachedEnvelopeArg)
    ) {
      throw new TypeError('TypedRequest received an invalid cached response envelope');
    }
    return {
      method: requestIdentityArg.method,
      request: payloadSendingArg.request,
      response: cachedEnvelopeArg.response,
      requestInstanceId: requestIdentityArg.requestInstanceId,
      correlation: {
        id: requestIdentityArg.correlationId,
        phase: 'response',
      },
    } as plugins.typedRequestInterfaces.ITypedRequest;
  }

  private createRequestIdentitySnapshot(
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest,
  ): IRequestIdentitySnapshot {
    return Object.freeze({
      method: payloadSendingArg.method,
      correlationId: payloadSendingArg.correlation?.id,
      requestInstanceId: payloadSendingArg.requestInstanceId,
    });
  }

  private isReusableCachedHttpEnvelope(
    payloadReceivingArg: plugins.typedRequestInterfaces.ITypedRequest,
  ): boolean {
    return !Object.hasOwn(payloadReceivingArg, 'error')
      && !Object.hasOwn(payloadReceivingArg, 'retry')
      && !VirtualStream.containsNetworkDescriptor(payloadReceivingArg);
  }

  private isAbortSignal(valueArg: unknown): valueArg is AbortSignal {
    return Boolean(
      valueArg
      && typeof valueArg === 'object'
      && typeof Reflect.get(valueArg, 'aborted') === 'boolean'
      && typeof Reflect.get(valueArg, 'addEventListener') === 'function'
      && typeof Reflect.get(valueArg, 'removeEventListener') === 'function'
    );
  }

  private async createCacheKey(
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest
  ): Promise<string> {
    const cacheIdentity = JSON.stringify({
      endpoint: this.urlEndPoint,
      method: payloadSendingArg.method,
      request: payloadSendingArg.request,
    });
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(cacheIdentity)
    );
    const digestHex = Array.from(new Uint8Array(digest), (byteArg) =>
      byteArg.toString(16).padStart(2, '0')
    ).join('');
    return `typedrequest:v1:${digestHex}`;
  }
}
